import { apiEndpoints } from "@repo/common";
import { httpsCallable as firebaseHttpsCallable, getFunctions, } from "firebase/functions";
const SECONDARY_REGION = "us-east1";
const RETRYABLE_CODES = new Set([
    "functions/unavailable",
    "functions/deadline-exceeded",
    "functions/internal",
]);
const usingEmulator = typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1") &&
    // biome-ignore lint/suspicious/noExplicitAny: matches Firebase emulator setup
    import.meta.env?.VITE_USE_FIREBASE_EMULATORS === "true";
/** Resolve the direct regional API URL used when the public API is unavailable. */
export function apiFallbackBaseUrl(primaryBaseUrl, environment) {
    const hostname = new URL(primaryBaseUrl).hostname;
    return hostname === "localhost" || hostname === "127.0.0.1"
        ? primaryBaseUrl
        : apiEndpoints[environment].regionalBaseUrls[SECONDARY_REGION];
}
function retryable(error) {
    const code = error && typeof error === "object" && "code" in error
        ? String(error.code)
        : "";
    return RETRYABLE_CODES.has(code);
}
/** Retry a callable in the second region only when the first region failed. */
export function httpsCallable(functions, name, options) {
    const primary = firebaseHttpsCallable(functions, name, options);
    const secondary = usingEmulator
        ? null
        : firebaseHttpsCallable(getFunctions(functions.app, SECONDARY_REGION), name, options);
    const call = async (data) => {
        try {
            return await primary(data);
        }
        catch (error) {
            if (!secondary || !retryable(error))
                throw error;
            return secondary(data);
        }
    };
    return Object.assign(call, {
        stream: async (data, options) => {
            try {
                return await primary.stream(data, options);
            }
            catch (error) {
                if (!secondary || !retryable(error))
                    throw error;
                return secondary.stream(data, options);
            }
        },
    });
}
/** Use for replayable HTTP requests to function endpoints. */
export async function fetchWithRegionalFallback(urls, init, timeoutMs) {
    const attempt = async (url) => {
        if (init?.signal?.aborted)
            throw init.signal.reason;
        if (!timeoutMs)
            return fetch(url, init);
        const controller = new AbortController();
        const cancel = () => controller.abort();
        init?.signal?.addEventListener("abort", cancel, { once: true });
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            return await fetch(url, { ...init, signal: controller.signal });
        }
        finally {
            clearTimeout(timer);
            init?.signal?.removeEventListener("abort", cancel);
        }
    };
    let response;
    try {
        response = await attempt(urls[0]);
    }
    catch (error) {
        if (init?.signal?.aborted)
            throw error;
        return attempt(urls[1]);
    }
    return response.status >= 500 && urls[0] !== urls[1]
        ? attempt(urls[1])
        : response;
}
